import { useTranslation } from 'react-i18next';
import Fund from '@/models/Fund';
import useFundContext from '@/contexts/Fund/useFundContext';
import FundSelect from '@/components/FundSelect/FundSelect';
import CustomTooltip from '@/components/ui/Tooltip/Tooltip';
import TextRowsSkeleton from '@/components/ui/Skeleton/TextRowsSkeleton';
import RefreshControl from '@/components/ui/RefreshControl/RefreshControl';
import useGlobalRefresh from '@/hooks/useGlobalRefresh';
import useFocusRefresh from '@/hooks/useFocusRefresh';
import tooltipQuestion from '@/assets/icons/tooltip-question.svg';
import { buildAvailableBalancePendingTooltipText } from '@/helpers/availableBalanceTooltipText';
import ViewOnlyChip from './ViewOnlyChip';
import IsraeliIdGate from './IsraeliIdGate';
import styles from './FundBalanceBar.module.scss';

/** Brief pause after a board refresh so rapid repeat taps do not stack refetches (SPR-1229 follow-up). */
const BOARD_REFRESH_COOLDOWN_MS = 1500;

interface FundBalanceBarProps {
  funds: Fund[];
  isLoading: boolean;
  onSelectFund: (fundId: number) => void;
}

/**
 * הפס שבראש כל מסך של קרן: באיזו קרן אנחנו, ומה היתרה.
 *
 * שני הדברים יושבים כאן ולא בתפריט, כי בתפריט הם נעלמים בדיוק כשצריך אותם —
 * הוא מכווץ לרייל של אייקונים במסך צר, ובנייד הוא מגירה סגורה כמעט תמיד.
 * הבורר כאן הוא הבורר היחיד באתר; אין עוד עותק שלו בתפריט.
 *
 * הפס נשאר תמיד באותו גובה. אין לו מצב מקופל, וגם היתרה הזמינה מוצגת תמיד:
 * שני גבהים לאותו מידע רק הזיזו את הלוח מתחת ליד של מי שקרא אותו.
 *
 * צבע היתרה בא מלוח התנועות: חיובית בצבע התרומה, שלילית בצבע התמיכה — אותם
 * טוקנים שעמודת סוג התנועה נותנת לשורת תרומה ולשורת תמיכה.
 */
const FundBalanceBar = ({ funds, isLoading, onSelectFund }: FundBalanceBarProps) => {
  const { t, i18n } = useTranslation();
  const { currentFund, balance } = useFundContext();
  /*
   * רענון האזור האישי יושב כאן ולא בסרגל של לוח מסוים (HAT-1237): המדווח ביקש
   * לרענן "את האזור האישי", והפס הזה הוא הדבר היחיד שמרונדר מעל כל מסך בקרן.
   * לשונית שתיווסף בעתיד מקבלת את הכפתור בלי שורת קוד, ורק צריכה להצהיר על
   * השאילתות שלה (ר' useGlobalRefresh).
   */
  const globalRefresh = useGlobalRefresh();
  const { refresh, isRefreshing, updatedAt, hasRefreshable } = globalRefresh;
  /*
   * ואותו רענון בדיוק גם בחזרה ללשונית (HAT-1315). כאן, ולא על שאילתה בודדת,
   * כדי שלא ייווצר שוב מצב שבו האוטומטי מושך פחות מהידני - ר' useFocusRefresh.
   */
  useFocusRefresh(globalRefresh);

  // לרכז של קרן אחת אין מה לבחור, ודרופדאון מת רק מזמין קליק לשום מקום.
  const hasChoice = isLoading || funds.length !== 1;

  const format = (amount: number) =>
    amount
      .toLocaleString(i18n.language, {
        style: 'currency',
        currency: balance?.currency ?? 'ILS',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      .replace(/[\u200e\u200f\u061c]/g, '');

  const total = balance?.balancesByKind?.total ?? 0;
  const available = balance?.calculateAvailableBalance();

  const tooltipText =
    balance != null
      ? buildAvailableBalancePendingTooltipText(t, format, balance.supportRequestsBalances)
      : '';

  const amountClassName = `${styles.primaryAmount} ${total > 0 ? styles.positive : ''} ${total < 0 ? styles.negative : ''}`;
  /*
   * HAT-1218: במסך צר מוצגת רק "יתרה זמינה" (ר' _module.scss מתחת ל-$tablet).
   * הצבע נקבע לפי הסימן של הסכום הזמין — חיובי ירוק, שלילי בורדו — ולא לפי
   * `total` שהיה קובע צבע ליתרה שאפילו לא רואים.
   */
  const availableClassName = `${styles.secondary} ${available != null && available > 0 ? styles.secondaryPositive : ''} ${available != null && available < 0 ? styles.secondaryNegative : ''}`;

  return (
    <div className={`${styles.bar} ph-mask`} data-fund-balance-bar>
      <div className={styles.identity}>
        {/* "תצוגת נתונים לקרן:" — התווית שמעל הבורר במקור (.fund-picker-label).
            מוצגת במסך צר בלבד; ברוחב מלא הבורר מסביר את עצמו. */}
        <span className={styles.pickerLabel}>{t('viewDataForFund')}:</span>
        {hasChoice ? (
          <FundSelect
            variant="bar"
            funds={funds}
            historyFunds={[]} //TODO: fix this according to backend changes
            currentFund={currentFund}
            handleSelectChange={onSelectFund}
            isLoading={isLoading}
          />
        ) : (
          <span className={styles.fundName} title={currentFund?.name}>
            {currentFund?.id}
            {currentFund?.name ? ` | ${currentFund.name}` : ''}
          </span>
        )}
        {currentFund && <ViewOnlyChip fundId={currentFund.id} />}
        {currentFund && <IsraeliIdGate fundId={currentFund.id} fundName={currentFund.name} />}
      </div>

      {balance == null ? (
        <TextRowsSkeleton rows={[{ width: '180px', height: '22px' }]} rowsGap={0} />
      ) : (
        <>
          <div className={styles.divider} aria-hidden="true" />
          <div className={styles.group}>
            <span className={styles.label}>{t('fundBalance')}</span>
            <span className={amountClassName} data-testid="fund-balance">
              <span className={styles.dot} aria-hidden="true" />
              <bdi dir="ltr">{format(total)}</bdi>
            </span>
          </div>

          {available != null && (
            <>
              <div className={styles.divider} aria-hidden="true" />
              <div className={availableClassName}>
                <span className={styles.secondaryLabel}>{t('fundAvailableBalance')}</span>
                <bdi dir="ltr" className={styles.secondaryAmount} data-testid="available-balance">
                  {format(available)}
                </bdi>
                <img id="fundBalanceBarAvailable" src={tooltipQuestion} alt="" width={16} />
                <CustomTooltip targetId="fundBalanceBarAvailable">
                  <p className="body-sm-regular text-(--font-sm-title)" style={{ whiteSpace: 'pre-line' }}>
                    {tooltipText}
                  </p>
                </CustomTooltip>
              </div>
            </>
          )}
        </>
      )}

      {/* בקצה הפס, אחרי הזהות והיתרה: רענון של כל מה שהמסך הנוכחי מציג. */}
      {hasRefreshable && (
        <div className={styles.refresh}>
          <RefreshControl
            onRefresh={refresh}
            isRefreshing={isRefreshing}
            updatedAt={updatedAt}
            cooldownMs={BOARD_REFRESH_COOLDOWN_MS}
          />
        </div>
      )}
    </div>
  );
};

export default FundBalanceBar;
